/**
 * BLU-711 A2 — every outbound message is written for the reviewer and shown
 * before it sends.
 *
 * Three families, all editable in the composer before they go:
 *   1. `buildAskMessage`      — a question raised while stepping through a flag.
 *   2. `buildDecisionSummary` — what was accepted / recounted / rejected, sent
 *                               with the approval.
 *   3. `buildRejectMessage`   — the whole count went back for a recount.
 *
 * `buildPingText` is the ONLY thing that ever leaves the app. Slack and
 * copy-to-clipboard send that line and nothing else, so figures never land in
 * a channel and the conversation stays in one thread (handover §7).
 *
 * Figures are BULLET LINES (`- SKU: expected 50, counted 0 (-50)`), not a
 * space-padded table: the bubble renders in a proportional font, where padded
 * columns collapse into a wall (owner feedback 2026-08-05). The thread renders
 * any run of `- ` lines as a real list (`MessageBody`), and the body stays
 * plain text so it is still editable in the composer textarea.
 *
 * Every generated message also declares the SKUs it is about
 * (`askMessageSkus` / `decisionSummarySkus`) so it carries the same
 * click-to-jump reference chip a hand-attached message gets.
 */
import type { TFunction } from 'i18next';
import { STOCKTAKE_MESSAGE_MAX_SKUS, type StocktakeProposalLineRow } from '@threadheads/shared';
import { dropPercent, type FlagCard } from './flag-cards';
import { formatQty, formatSigned } from './format';

/** The thread caps a message at 4000 chars (`postStocktakeMessageSchema`), and
 *  a wall of 500 SKUs is unreadable long before that. Longer lists are cut with
 *  a count, and the full detail stays on the count itself. */
const MAX_LISTED_LINES = 25;

export interface MessageContext {
  t: TFunction;
  /** The counterparty's display name, e.g. "Thread Lab". */
  org: string;
  locationName: string;
  /** Already-formatted count date. */
  countedOn: string;
}

/** One bullet line. The separators live in the locale string so zh can use its
 *  own punctuation instead of inheriting English commas. */
function figureRow(t: TFunction, line: StocktakeProposalLineRow, trailing: string): string {
  const expected = t('stocktakes.msg.ask.rowExpected', { qty: formatQty(line.expectedQty) });
  const counted =
    line.countedQty == null
      ? t('stocktakes.msg.ask.rowCountedBlank')
      : t('stocktakes.msg.ask.rowCounted', { qty: formatQty(line.countedQty) });
  return t('stocktakes.msg.ask.row', {
    sku: line.sku ?? '',
    expected,
    counted,
    trailing,
  });
}

function delta(line: StocktakeProposalLineRow): number {
  return line.countedQty == null ? 0 : line.countedQty - line.expectedQty;
}

/** Deduped, capped at the same limit the thread enforces server-side. */
function skuCodes(lines: StocktakeProposalLineRow[]): string[] {
  const codes = new Set<string>();
  for (const line of lines) if (line.sku) codes.add(line.sku);
  return [...codes].slice(0, STOCKTAKE_MESSAGE_MAX_SKUS);
}

/** The rows an ask is about — the flag card's own lines (every blank line for
 *  an uncounted card, not just the handful the body names). */
export function askMessageSkus(card: FlagCard): string[] {
  return skuCodes(card.lines);
}

/**
 * The rows a decision summary names: recount + rejected. An approval that
 * names nothing attaches nothing, so a clean apply keeps a bare bubble instead
 * of a reference chip listing every accepted line.
 */
export function decisionSummarySkus(lines: StocktakeProposalLineRow[]): string[] {
  return skuCodes(
    lines.filter((line) => line.decision === 'recount' || line.decision === 'reject'),
  );
}

export function buildAskMessage(card: FlagCard, ctx: MessageContext): string {
  const { t, org, locationName, countedOn } = ctx;

  if (card.kind === 'mirror') {
    const rows = card.lines
      .map((line) => figureRow(t, line, formatSigned(delta(line))))
      .join('\n');
    return t('stocktakes.msg.ask.mirror', { org, location: locationName, date: countedOn, rows });
  }

  if (card.kind === 'drop') {
    const line = card.lines[0];
    const rows = line
      ? figureRow(
          t,
          line,
          t('stocktakes.msg.ask.dropDelta', {
            delta: formatSigned(delta(line)),
            pct: dropPercent(line),
          }),
        )
      : '';
    return t('stocktakes.msg.ask.drop', { org, location: locationName, date: countedOn, rows });
  }

  const codes = card.lines.map((line) => line.sku).filter((sku): sku is string => !!sku);
  const first = codes.slice(0, 3).join(', ');
  const skus =
    codes.length > 3
      ? t('stocktakes.msg.ask.uncountedMore', { skus: first, count: codes.length - 3 })
      : first;
  // Split intro from body so the count-bearing sentence can carry a plural
  // form without duplicating the whole block in every locale.
  return [
    t('stocktakes.msg.ask.uncountedIntro', {
      org,
      location: locationName,
      date: countedOn,
      count: card.lines.length,
    }),
    t('stocktakes.msg.ask.uncountedBody', { skus }),
  ].join('\n\n');
}

export function buildDecisionSummary(
  lines: StocktakeProposalLineRow[],
  ctx: MessageContext,
): string {
  const { t, org, locationName, countedOn } = ctx;
  const accepted = lines.filter((line) => line.decision === 'accept' && line.countedQty != null);
  const recount = lines.filter((line) => line.decision === 'recount');
  const rejected = lines.filter((line) => line.decision === 'reject');

  const lineText = (line: StocktakeProposalLineRow) =>
    t('stocktakes.msg.decision.line', {
      sku: line.sku ?? '',
      expected: formatQty(line.expectedQty),
      counted:
        line.countedQty == null
          ? t('stocktakes.msg.decision.blank')
          : formatQty(line.countedQty),
    });

  const listed = (lines: StocktakeProposalLineRow[]) => {
    const shown = lines.slice(0, MAX_LISTED_LINES).map(lineText);
    if (lines.length > MAX_LISTED_LINES) {
      shown.push(
        t('stocktakes.msg.decision.andMore', { count: lines.length - MAX_LISTED_LINES }),
      );
    }
    return shown;
  };

  const blocks: string[] = [
    t('stocktakes.msg.decision.intro', { org, location: locationName, date: countedOn }),
    t('stocktakes.msg.decision.accepted', { count: accepted.length }),
  ];
  if (recount.length > 0) {
    blocks.push(
      [
        t('stocktakes.msg.decision.recountHeader', { count: recount.length }),
        ...listed(recount),
      ].join('\n'),
    );
  }
  if (rejected.length > 0) {
    blocks.push(
      [
        t('stocktakes.msg.decision.rejectHeader', { count: rejected.length }),
        ...listed(rejected),
      ].join('\n'),
    );
  }
  blocks.push(t('stocktakes.msg.decision.outro'));
  return blocks.join('\n\n');
}

export function buildRejectMessage(ctx: MessageContext): string {
  return ctx.t('stocktakes.msg.reject.body', {
    org: ctx.org,
    location: ctx.locationName,
    date: ctx.countedOn,
  });
}

/** The whole of what an outside channel ever receives. */
export function buildPingText(
  ctx: Pick<MessageContext, 't' | 'locationName' | 'countedOn'>,
  senderOrg: string,
  proposalId: string,
): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  return ctx.t('stocktakes.msg.ping', {
    sender: senderOrg,
    location: ctx.locationName,
    date: ctx.countedOn,
    url: `${origin}/inventory/stocktakes?proposal=${proposalId}`,
  });
}
